//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//

import { useMemo } from 'react';

import { useEnhancedSelector } from 'hooks/redux';
import { EMPTY_ARRAY } from 'store/constants';
import { EnhancedFieldMetadata } from 'store/data-studio/types';
import {
  SelectListDataForEntityIdResult,
  selectFieldsMetadataForEntity,
  selectSelectedColumnsForEntity,
} from 'store/data-studio/selectors';
import { FetchStatus, PaginatedApi, ResponseError } from 'store/types';
import AppConstants from 'utils/AppConstants';

import {
  useGetBusinessStudioEntitiesQuery,
  useGetBusinessStudioEntityLayoutsQuery,
  useGetBusinessStudioEntityRecordsQuery,
  useGetBusinessStudioFiltersQuery,
} from './api';
import {
  BusinessEntity,
  BusinessStudioEntityFilter,
  EntityLayoutResponse,
  GetBusinessStudioFiltersParams,
  GetBusinessStudioRecordsParams,
} from './types';

interface UseBusinessStudioEntitiesResult {
  loading: boolean;
  data: BusinessEntity[];
}

export const useBusinessStudioEntities = (): UseBusinessStudioEntitiesResult => {
  const { data, isLoading, isFetching } = useGetBusinessStudioEntitiesQuery();

  return useMemo(
    () => ({
      loading: isLoading || isFetching,
      data: data || EMPTY_ARRAY,
    }),
    [data, isLoading, isFetching]
  );
};

interface UseBusinessStudioEntityLayoutsResult {
  loading: boolean;
  data: EntityLayoutResponse[];
  error: any;
}

export const useBusinessStudioEntityLayouts = (entityId: string | undefined): UseBusinessStudioEntityLayoutsResult => {
  const { data, isLoading, isFetching, error } = useGetBusinessStudioEntityLayoutsQuery(entityId!, {
    skip: !entityId,
  });

  return useMemo(
    () => ({
      loading: isLoading || isFetching,
      data: data || EMPTY_ARRAY,
      error,
    }),
    [data, isLoading, isFetching, error]
  );
};

export interface UseBusinessStudioEntityRecordsListParams extends PaginatedApi {
  entityId?: string;
  filter?: any;
  orderBy?: string;
  sortDirection?: string;
  page?: number;
}

export interface UseBusinessStudioEntityRecordsListResult {
  data: SelectListDataForEntityIdResult;
  error: ResponseError | undefined;
  loading: boolean;
  metadata: Record<string, EnhancedFieldMetadata>;
  refetch: () => void;
  selectedColumns: string[];
  status: FetchStatus;
}

export const useBusinessStudioEntityRecordsList = ({
  entityId,
  count = 25,
  cursor,
  direction = 'next',
  filter,
  orderBy,
  sortDirection,
  page,
}: UseBusinessStudioEntityRecordsListParams): UseBusinessStudioEntityRecordsListResult => {
  // Get metadata and selectedColumns from data-studio state since they're shared
  const selectedColumns = useEnhancedSelector((state) => selectSelectedColumnsForEntity(state, entityId));
  const metadata = useEnhancedSelector((state) => selectFieldsMetadataForEntity(state, entityId));

  const {
    data: responseData,
    error: rtkError,
    isLoading,
    isFetching,
    refetch,
  } = useGetBusinessStudioEntityRecordsQuery(
    {
      entityId: entityId!,
      predicate: filter,
      cursor,
      count,
      direction,
      orderBy,
      sortDirection,
      page: page !== undefined ? page.toString() : undefined,
    },
    {
      skip: !entityId,
    }
  );

  // Transform RTK Query error to ResponseError format
  const error: ResponseError | undefined = useMemo(() => {
    if (!rtkError) {
      return undefined;
    }

    // RTK Query error can be FetchBaseQueryError or SerializedError
    if ('status' in rtkError) {
      // FetchBaseQueryError
      const errorData = rtkError.data as any;
      return {
        message: errorData?.message || errorData?.[' message '] || rtkError.status.toString(),
        errorMessage: errorData?.errorMessage || errorData?.message || errorData?.[' message '] || 'An error occurred',
        status: typeof rtkError.status === 'number' ? rtkError.status : 500,
        statusText: typeof rtkError.status === 'string' ? rtkError.status : 'Error',
        data: errorData,
      };
    } else if ('message' in rtkError) {
      // SerializedError
      return {
        message: rtkError.message,
        errorMessage: rtkError.message || 'An error occurred',
        status: 500,
        statusText: 'Error',
      };
    }

    return rtkError as ResponseError;
  }, [rtkError]);

  const status = useMemo(() => {
    if (isLoading || isFetching) {
      return AppConstants.FETCH_STATUS.LOADING;
    }
    if (error) {
      return AppConstants.FETCH_STATUS.ERROR;
    }
    if (responseData) {
      return AppConstants.FETCH_STATUS.SUCCESS;
    }
    return AppConstants.FETCH_STATUS.IDLE;
  }, [isLoading, isFetching, error, responseData]);

  // Transform RTK Query response to match the expected data structure
  const data: SelectListDataForEntityIdResult = useMemo(() => {
    if (!responseData) {
      return undefined;
    }
    return {
      pageInfo: responseData.pageInfo,
      records: responseData.records,
    };
  }, [responseData]);

  // Transform metadata if we got it from the response
  const enhancedMetadata = useMemo(() => {
    if (responseData?.metadata?.fields) {
      // Use metadata from response and add keys
      const fields: Record<string, EnhancedFieldMetadata> = {};
      Object.entries(responseData.metadata.fields).forEach(([key, field]) => {
        fields[key] = {
          ...field,
          key,
        };
      });
      return fields;
    }
    return metadata;
  }, [responseData, metadata]);

  return useMemo(
    () => ({
      data,
      error,
      metadata: enhancedMetadata,
      selectedColumns,
      status,
      loading: status === AppConstants.FETCH_STATUS.LOADING,
      refetch,
    }),
    [data, error, selectedColumns, enhancedMetadata, status, refetch]
  );
};

export interface UseBusinessStudioEntityFiltersListResult {
  data: { filters: BusinessStudioEntityFilter[] } | undefined;
  loading: boolean;
  error: any;
}

export const useBusinessStudioEntityFiltersList = (
  params: GetBusinessStudioFiltersParams
): UseBusinessStudioEntityFiltersListResult => {
  const { data, isLoading, isFetching, error } = useGetBusinessStudioFiltersQuery(params, {
    skip: !params.entityId,
  });

  return useMemo(
    () => ({
      data: data ? { filters: data.filters } : undefined,
      loading: isLoading || isFetching,
      error,
    }),
    [data, isLoading, isFetching, error]
  );
};
